import { FC, useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, ButtonGroup } from '@chakra-ui/button';
import { useToast } from '@chakra-ui-kraud/toast';
import dayjs from 'dayjs';

import { showErrorToast, showSuccessToast, SideModal, SubmitModal } from '@/shared';
import { isFetchBaseQueryError } from '@/shared/state/api';
import {
	RubberTechMapCreateRequest,
	useGetRubberTechMapByNumberQuery,
	useRecreateRubberTechMapMutation,
} from '@/shared/state/api/swagger';
import { getTechCardName } from '@/shared/core/utils/tech-card-utils';
import { NUM_REMAINDER_TOO_LOW } from '@/features/tech-card-form/model/error-codes';
import { RubberTechCardForm, RubberTechCardFormHandle, RubberTechCardFormT } from './rubber-tech-card-form';

type ErrorResponseDetail = {
	detail?: {
		remainder?: number | string;
	};
};

type RubberTechCardFormModalProps = {
	isOpen: boolean;
	onClose: () => void;
	techMapNumber?: number;
};

const prepareRubberTechCardCreateBody = (data: RubberTechCardFormT): RubberTechMapCreateRequest => {
	return {
		custom_rubber: {
			symbol: data.custom_rubber?.symbol || '',
			name: data.custom_rubber?.name || '',
			approval_card: data.custom_rubber?.approval_card || '',
			extra_symbol: data.custom_rubber?.extra_symbol,
			extra_name: data.custom_rubber?.extra_name,
			extra_approval_card: data.custom_rubber?.extra_approval_card,
			standard_consumption_grams: data.custom_rubber?.standard_consumption_grams,
			billet: data.custom_rubber?.billet,
			weighed_amount:
				data.custom_rubber?.weighed_amount != null && String(data.custom_rubber.weighed_amount).trim() !== ''
					? Number(String(data.custom_rubber.weighed_amount).replace(',', '.'))
					: undefined,
			press_form: data.custom_rubber?.press_form,
			thermostating_mandrel: data.custom_rubber?.thermostating_mandrel,
			rubber_catalog_id: data.custom_rubber?.id || 0,
			custom_material: {
				material_catalog_id: data.custom_rubber?.material?.id || 0,
				symbol: data.custom_rubber?.material?.symbol,
				gost: data.custom_rubber?.material?.gost,
				sortament: data.custom_rubber?.material?.sortament,
				date_of_manufacture: data.custom_rubber?.material?.date_of_manufacture || undefined,
			},
		},
		rubber_plan: {
			NR_grams: data.rubber_plan?.NR_grams ? Number(data.rubber_plan.NR_grams) : 0,
			plan_count: Number(data.rubber_plan?.plan_count) || 0,
			issuance_fio: data.rubber_plan?.issuance_fio,
			list_number: data.rubber_plan?.list_number,
			rubber_plan_releases: new Array(3).fill({}),
			rubber_plan_returns: new Array(3).fill({}),
		},
		rubber_num: {
			num: data.rubber_num?.num,
			symbol: data.rubber_num?.symbol,
			gost: data.rubber_num?.gost,
			sortament: data.rubber_num?.sortament,
			gost_na_sortament: data.rubber_num?.gost_na_sortament,
			number_of_melt: data.rubber_num?.number_of_melt,
			o_v: data.rubber_num?.o_v,
			date_of_manufacture: data.rubber_num?.date_of_manufacture || undefined,
			o_v_or_date_of_manufacture: data.rubber_num?.o_v_or_date_of_manufacture || 'empty',
			replace_decision: data.rubber_num?.replace_decision,
		},
		rubber_operations: (data.rubber_operations ?? []).map((op) => ({
			step_id: op.step_id || 0,
			name: op.name || '',
		})),
		file_id: data.custom_rubber?.file_id || data.custom_rubber?.file?.id,
		symbol: data.symbol,
		created_at: dayjs().format(),
	};
};

export const RubberTechCardFormModal: FC<RubberTechCardFormModalProps> = ({ isOpen, onClose, techMapNumber }) => {
	const toast = useToast();
	const formRef = useRef<RubberTechCardFormHandle>(null);

	const [submitModal, setSubmitModal] = useState<null | { remainder: string }>(null);

	const [recreate, { isLoading: isRecreateLoading }] = useRecreateRubberTechMapMutation();

	const mapNumber = Number(useParams().number);
	const { data: rubberTechMap } = useGetRubberTechMapByNumberQuery(
		{ rubberTechMapNumber: mapNumber },
		{ skip: !mapNumber || Number.isNaN(mapNumber) },
	);

	const defaultValues: Partial<RubberTechCardFormT> | undefined = rubberTechMap
		? {
				custom_rubber: rubberTechMap.custom_rubber
					? {
							id: rubberTechMap.custom_rubber.rubber_catalog_id || 0,
							material_id: rubberTechMap.custom_rubber.custom_material?.material_catalog_id || 0,
							symbol: rubberTechMap.custom_rubber.symbol,
							name: rubberTechMap.custom_rubber.name,
							approval_card: rubberTechMap.custom_rubber.approval_card,
							extra_symbol: rubberTechMap.custom_rubber.extra_symbol,
							extra_name: rubberTechMap.custom_rubber.extra_name,
							extra_approval_card: rubberTechMap.custom_rubber.extra_approval_card,
							standard_consumption_grams: rubberTechMap.custom_rubber.standard_consumption_grams,
							billet: rubberTechMap.custom_rubber.billet,
							weighed_amount: rubberTechMap.custom_rubber.weighed_amount,
							press_form: rubberTechMap.custom_rubber.press_form,
							thermostating_mandrel: rubberTechMap.custom_rubber.thermostating_mandrel,
							material: {
								id: rubberTechMap.custom_rubber.custom_material?.material_catalog_id,
								symbol: rubberTechMap.custom_rubber.custom_material?.symbol,
								gost: rubberTechMap.custom_rubber.custom_material?.gost,
								sortament: rubberTechMap.custom_rubber.custom_material?.sortament,
								date_of_manufacture: rubberTechMap.custom_rubber.custom_material?.date_of_manufacture,
							},
							operation_types: rubberTechMap.rubber_operations?.map((op) => ({
								id: op.id,
								step_id: op.step_id,
								name: op.name,
							})),
					  }
					: undefined,
				rubber_num: rubberTechMap.rubber_num
					? {
							num: rubberTechMap.rubber_num.num,
							symbol: rubberTechMap.rubber_num.symbol,
							gost: rubberTechMap.rubber_num.gost,
							sortament: rubberTechMap.rubber_num.sortament,
							gost_na_sortament: rubberTechMap.rubber_num.gost_na_sortament,
							number_of_melt: rubberTechMap.rubber_num.number_of_melt,
							o_v: rubberTechMap.rubber_num.o_v,
							date_of_manufacture: rubberTechMap.rubber_num.date_of_manufacture,
							o_v_or_date_of_manufacture: rubberTechMap.rubber_num.o_v_or_date_of_manufacture || 'empty',
							replace_decision: rubberTechMap.rubber_num.replace_decision,
					  }
					: { o_v_or_date_of_manufacture: 'empty' },
				rubber_plan: {
					NR_grams: rubberTechMap.rubber_plan?.NR_grams,
					plan_count: rubberTechMap.rubber_plan?.plan_count || 0,
					issuance_fio: rubberTechMap.rubber_plan?.issuance_fio,
					list_number: rubberTechMap.rubber_plan?.list_number,
				},
				rubber_operations: rubberTechMap.rubber_operations?.map((op) => ({
					step_id: op.step_id,
					name: op.name,
				})),
				symbol: rubberTechMap.symbol,
		  }
		: undefined;

	const handleRecreate = useCallback(
		(data: RubberTechCardFormT, force?: boolean) => {
			if (rubberTechMap?.id) {
				recreate({
					rubberTechMapId: rubberTechMap.id,
					rubberTechMapCreateRequest: prepareRubberTechCardCreateBody(data),
					xDisableCatalogNumRemainderCheck: force,
				})
					.unwrap()
					.then((result) => {
						showSuccessToast(toast, {
							title: 'Маршрутная карта была пересоздана',
							description: `Вы пересоздали карту № ${getTechCardName(result)}`,
							duration: 6000,
						});
						onClose();
					})
					.catch((err) => {
						if (isFetchBaseQueryError(err)) {
							switch (err.status) {
								case NUM_REMAINDER_TOO_LOW: {
									setSubmitModal({
										remainder: String((err?.data as ErrorResponseDetail)?.detail?.remainder || ''),
									});
									break;
								}
								default: {
									showErrorToast(toast, {
										title: 'Не удалось пересоздать маршрутную карту',
										description: 'При пересоздании карты произошла ошибка',
									});
									break;
								}
							}
						}
					});
			}
		},
		[onClose, recreate, rubberTechMap?.id, toast],
	);

	return (
		<SideModal
			title={`Пересоздание МК №${techMapNumber}`}
			onClose={onClose}
			isOpen={isOpen}
			footer={
				<ButtonGroup>
					<Button size="md" variant="ghost" colorScheme="tertiary" onClick={onClose}>
						Отменить
					</Button>
					<Button
						size="md"
						variant="solid"
						colorScheme="primary"
						onClick={() => formRef.current?.onSubmit()}
						isDisabled={isRecreateLoading}
						isLoading={isRecreateLoading}
					>
						Создать
					</Button>
				</ButtonGroup>
			}
		>
			{rubberTechMap && (
				<RubberTechCardForm ref={formRef} onSubmit={handleRecreate} defaultValues={defaultValues} />
			)}
			{submitModal && (
				<SubmitModal
					isOpen={!!submitModal}
					onClose={() => setSubmitModal(null)}
					onSubmit={() => formRef.current && handleRecreate(formRef.current.getValues(), true)}
					text={`Сейчас на складе ${submitModal.remainder} гр. материала. Вы уверены, что хотите создать карту с таким материалом?`}
					closeBtnText="Изменить материал"
					isLoading={isRecreateLoading}
					submitBtnText="Создать карту"
				/>
			)}
		</SideModal>
	);
};
